import React, { useState } from 'react'
import { DatePicker, message } from 'antd'
import { Button } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import { isNil } from 'lodash'
import { InvoiceDeliveryItemDto } from '../../api/dto/invoice-delivery.dto'
import { useUpdateInvoiceDeliveryDueDateMutation } from '../../api/invoice-delivery-api-client'
import {
  DrawerInlineEdit,
  DrawerInlineEditHint,
  DrawerInlineEditLabel,
  DrawerInlineEditRow,
} from './InvoiceDeliveryDrawer.styles'
import { DATE_INPUT_FORMATS, DUE_DATE_WIRE_FORMAT, hasBeenSent } from './utils'

interface DueDateEditorProps {
  delivery: InvoiceDeliveryItemDto
  isHistorical: boolean
  onUpdated: (delivery: InvoiceDeliveryItemDto) => void
}

/**
 * Per-invoice due date. Derived from the client's due day on upload, so
 * editing here only affects this invoice. Before the invoice is sent the new
 * date is what gets emailed; afterwards it only corrects what the customer's
 * portal shows.
 */
const DueDateEditor: React.FC<DueDateEditorProps> = ({
  delivery,
  isHistorical,
  onUpdated,
}) => {
  // `undefined` means untouched. There is no cleared state: every invoice
  // carries a due date, so the picker only ever swaps one date for another.
  const [draft, setDraft] = useState<string | undefined>(undefined)
  const mutation = useUpdateInvoiceDeliveryDueDateMutation({
    onSuccess: (updated) => {
      message.success('Due date saved')
      setDraft(undefined)
      onUpdated(updated)
    },
    onError: (err) => {
      message.error(err.message || 'Failed to save due date')
    },
  })

  const current = delivery.dueDate ?? null
  const value = draft === undefined ? current : draft
  const isDirty = draft !== undefined && draft !== current
  const isSent = hasBeenSent(delivery)

  const handleSave = () => {
    if (isNil(value)) return
    mutation.mutate({ id: delivery.id, body: { dueDate: value } })
  }

  return (
    <DrawerInlineEdit>
      <DrawerInlineEditLabel htmlFor={`due-date-${delivery.id}`}>
        Due date
      </DrawerInlineEditLabel>
      <DrawerInlineEditRow>
        <DatePicker
          value={isNil(value) ? undefined : dayjs(value)}
          allowClear={false}
          onChange={(date) => {
            if (isNil(date)) return
            setDraft(date.format(DUE_DATE_WIRE_FORMAT))
          }}
          id={`due-date-${delivery.id}`}
          format={DATE_INPUT_FORMATS}
          placeholder="Not set"
          disabled={isHistorical || mutation.isPending}
        />
        <Button
          type="primary"
          onClick={handleSave}
          loading={mutation.isPending}
          disabled={isHistorical || !isDirty}
        >
          Save
        </Button>
      </DrawerInlineEditRow>
      <DrawerInlineEditHint>
        {isHistorical
          ? 'This is a historical version — only the latest version can be edited.'
          : isSent
            ? 'This invoice has already been emailed, so a change here updates the customer portal only.'
            : 'This date is what the emailed invoice will show.'}
      </DrawerInlineEditHint>
    </DrawerInlineEdit>
  )
}

export { DueDateEditor }
